import { useAuth0 } from '@auth0/auth0-react'
import { Box, Button, Typography } from '@mui/material'
import { useState } from 'react'
import type { IDocumentsRowData } from 'src/hooks/queries/vessels/documents/types'
import { invalidateVesselCertificateQueries } from '~hooks/queries/vessels/invalidate-vessel-certificate-queries'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { ModalFooter } from '~components/molecules/modal/modal.styles'
import { DialogBox } from '~pages/pages-behind-login/chartering/pre-fixture-page/components/dialog-box'
import { Z_INDEX } from '~types/z-index'
import { apiBase } from '~utils/base-url'
import { forceDateToZuluTimezone } from '~utils/dates'
import { RESTRICTED_CERTIFICATES_TYPE } from '../document-types'
import { TMDocumentModalDropzone } from './tm-document-modal-dropzone'
import { formatInvalidInput } from 'src/utils/invalid-input-message'

export const certificateIssuerOptions = [
  'class',
  'flag',
  'port State',
  'insurance',
  'other',
].map((certificateIssuer) => ({
  label: `${certificateIssuer.charAt(0).toUpperCase()}${certificateIssuer.slice(1)}`,
  value: certificateIssuer.replaceAll(' ', ''),
}))

export const certificateCategories = [
  'Registration & Ownership',
  'Classification',
  'Construction & Load Line',
  'SOLAS (Safety)',
  'ISM (Safety Management)',
  'ISPS (Security)',
  'MARPOL (Environmental)',
  'Crew & Labour',
  'Liability & Insurance',
  'Other (Operational)',
]

export type AUGFile = {
  file: File
  uuid: string
  documentType?: string
  documentName?: string
  documentCategories?: string[]
  isDigitalCertificate?: boolean
  certificateIssuer?: string
  certificateNumber?: string
  referenceNumber?: string
  hasNoExpiryDate?: boolean
  hasNoNextSurvey?: boolean
  issuedDate?: Date | null
  expiryDate?: Date | null
  nextSurveyDate?: Date | null
  lastSurveyDate?: Date | null
  remarks?: string
  tags?: string[]
}

export const fileIsValid = (file: AUGFile) => {
  if (!file.documentType) {
    return false
  }
  if (
    file.documentType === 'Secondary Certificates' ||
    file.documentType === 'Primary Certificates (Other)' ||
    file.documentType === RESTRICTED_CERTIFICATES_TYPE
  ) {
    return !!file.documentName
  }
  return (
    !!file.documentName &&
    !!file.documentCategories?.length &&
    !!file.issuedDate &&
    (file.hasNoExpiryDate || !!file.expiryDate) &&
    !!file.lastSurveyDate &&
    (file.hasNoNextSurvey || !!file.nextSurveyDate)
  )
}

export const TMDocumentModal = ({
  vesselId,
  vesselName,
  primaryDocumentNamesByType,
  refetchDocuments,
  isModalOpen,
  setIsModalOpen,
  existingDocumentTags = [],
}: {
  vesselId: string
  vesselName: string
  primaryDocumentNamesByType: IDocumentsRowData[]
  refetchDocuments: (() => void) | undefined
  isModalOpen: boolean
  setIsModalOpen: (isOpen: boolean) => void
  existingDocumentTags?: string[]
}) => {
  const { getAccessTokenSilently } = useAuth0()
  const { setStatus } = useAnimatedAlert()
  const [files, setFiles] = useState<AUGFile[]>([])

  const [isLoading, setIsLoading] = useState(false)

  const isFormValid = () => {
    if (files.length === 0) {
      return false
    }
    return files.every((file) => fileIsValid(file))
  }

  const save = async () => {
    try {
      setIsLoading(true)

      const promiseArray = files.map(async (file) => {
        const token = await getAccessTokenSilently()
        const formData = new FormData()
        formData.append('type', file.documentType!)
        formData.append('documentName', file.documentName!)
        formData.append(
          'digitalCertificate',
          file.isDigitalCertificate ? 'true' : 'false',
        )
        formData.append('noExpiryDate', file.hasNoExpiryDate ? 'true' : 'false')
        formData.append('noNextSurvey', file.hasNoNextSurvey ? 'true' : 'false')
        if (file.issuedDate) {
          formData.append(
            'issued',
            forceDateToZuluTimezone(file.issuedDate).toISOString(),
          )
        }
        if (file.expiryDate) {
          formData.append(
            'expires',
            forceDateToZuluTimezone(file.expiryDate).toISOString(),
          )
        }
        if (file.certificateNumber) {
          formData.append('certificateNumber', file.certificateNumber)
        }
        if (file.referenceNumber) {
          formData.append('referenceNumber', file.referenceNumber)
        }
        if (file.certificateIssuer) {
          formData.append('certificateIssuer', file.certificateIssuer)
        }
        if (file.lastSurveyDate) {
          formData.append(
            'lastSurvey',
            forceDateToZuluTimezone(file.lastSurveyDate).toISOString(),
          )
        }
        if (file.nextSurveyDate) {
          formData.append(
            'nextSurvey',
            forceDateToZuluTimezone(file.nextSurveyDate).toISOString(),
          )
        }
        if (file.remarks) {
          formData.append('remarks', file.remarks)
        }
        if (file.documentCategories?.length) {
          file.documentCategories.forEach((category: string) => {
            formData.append('certificateCategories', category)
          })
        }
        if (file.tags?.length) {
          file.tags.forEach((tag: string) => {
            formData.append('tags', tag)
          })
        }
        formData.append('file', file.file)

        const url = `${apiBase()}/vessels/${vesselId}/documents`

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        })

        const jsonRes: Response & { statusCode: number; message?: string } =
          await response.json()

        return {
          ...jsonRes,
          uuid: file.uuid,
          statusCode: jsonRes?.statusCode || 200,
        }
      })

      const responses = await Promise.all(promiseArray)

      setIsLoading(false)

      const failedResponses = responses.filter((res) => res.statusCode !== 200)
      if (failedResponses.length < responses.length) {
        invalidateVesselCertificateQueries(vesselId)
      }

      if (failedResponses.length === 0) {
        setStatus('Document(s) saved successfully', 7000, 'success')
        setIsModalOpen(false)
        setFiles([])
      } else {
        setFiles((prevFiles) =>
          prevFiles.filter((file) =>
            [...failedResponses.map((res) => res.uuid)].includes(file.uuid),
          ),
        )
        const invalidInput = failedResponses.find(
          (res) => res.statusCode === 400 && res.message,
        )?.message
        setStatus(
          invalidInput
            ? formatInvalidInput(invalidInput)
            : 'Some documents failed to save. Please try again.',
          12000,
          'error',
        )
      }

      refetchDocuments?.()
    } catch (err) {
      setStatus('Adding document(s) failed', 5000, 'error')
      setIsLoading(false)
    }
  }

  return (
    <DialogBox
      sx={{
        width: '1326px',
        maxWidth: 'none',
        zIndex: Z_INDEX.Popup,
        display: 'flex',
      }}
      open={isModalOpen}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return
        }
        setIsModalOpen(false)
      }}
    >
      <Box>
        <Typography variant="h2" sx={{ mb: 1 }}>
          {`Add certificate(s) ${vesselName ? `for ${vesselName}` : ''} `}
        </Typography>
        <Typography variant="caption">
          To add document(s), first select the file(s), choose document type and
          then fill in data.
        </Typography>
        <Box>
          <TMDocumentModalDropzone
            files={files}
            setFiles={setFiles}
            multiple
            primaryDocumentNamesByType={primaryDocumentNamesByType}
            existingDocumentTags={existingDocumentTags}
          />
        </Box>
        <ModalFooter>
          <Button
            variant="text"
            onClick={() => {
              setIsModalOpen(false)
              setFiles([])
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!isFormValid()}
            loading={isLoading}
            onClick={save}
          >
            Save
          </Button>
        </ModalFooter>
      </Box>
    </DialogBox>
  )
}
